import React, { useEffect, useState } from "react"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  InputGroup,
  Row,
  Spinner,
  Table,
} from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import Breadcrumbs from "components/Common/Breadcrumb"
import Paginations from "components/Common/Paginations"
import ConfirmModal from "components/Common/ConfirmModal"
import TableActionsDropdown from "components/Shared/TableActionsDropdown"
import isPermissionEnabled from "common/isPermissionEnabled"
import {
  saGetAllTenants,
  saLoginToTenant,
  saRefreshTenantPermissions,
  saUpdateTenantStatus,
} from "store/actions/superAdminAction"
import { showAlert } from "store/reducers/alertReducer"
import { ROUTES } from "constants/routes"
import { navigateToAccessibleRoute } from "utils/navigateUnauthorized"
import { translateStatusLabel } from "utils/statusLabels"

const STATUS_COLORS = {
  active: "success",
  suspended: "danger",
  inactive: "secondary",
  pending: "warning",
}

const normalizeTenantStatusLabel = tenant => {
  if (tenant?.statusLabel) {return tenant.statusLabel}
  if (tenant?.status === 1) {return "active"}
  if (tenant?.status === 0) {return "inactive"}
  if (tenant?.status === 3) {return "suspended"}
  if (tenant?.status === 4) {return "pending"}
  return String(tenant?.status || "").toLowerCase() || "inactive"
}

const TenantsList = () => {
  const { t } = useTranslation()
  document.title = t("Tenants | Super Admin")

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1

  const { userRole, accountType, accountScope } = useSelector(state => state.Login)
  const isSuperAdminSession =
    accountScope === "super_admin" || accountType === "superadmin"

  const { tenantsLoading, tenantActionLoading, tenants, tenantsTotalCount, tenantsTotalPages } =
    useSelector(state => state.SuperAdmin)
  const [loggingInTenantId, setLoggingInTenantId] = useState(null)
  const [refreshingTenantId, setRefreshingTenantId] = useState(null)

  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [tenantPermissions, setTenantPermissions] = useState({
    list: false,
    create: false,
    update: false,
    login: false,
  })
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    tenant: null,
    action: "",
  })

  const hasRowActions =
    tenantPermissions.list || tenantPermissions.update

  useEffect(() => {
    if (!isSuperAdminSession) {
      navigateToAccessibleRoute(navigate)
      return
    }
    const canList = isPermissionEnabled(
      userRole,
      104,
      "sa_tenants_1",
      accountType,
      accountScope
    )
    if (!canList) {
      navigateToAccessibleRoute(navigate)
      return
    }
    setTenantPermissions({
      list: canList,
      create: isPermissionEnabled(
        userRole,
        104,
        "sa_tenants_2",
        accountType,
        accountScope
      ),
      update: isPermissionEnabled(
        userRole,
        104,
        "sa_tenants_3",
        accountType,
        accountScope
      ),
      login: isPermissionEnabled(
        userRole,
        104,
        "sa_tenants_4",
        accountType,
        accountScope
      ),
    })
  }, [userRole, accountType, accountScope, isSuperAdminSession, navigate])

  useEffect(() => {
    if (!tenantPermissions.list) {
      return
    }
    dispatch(
      saGetAllTenants({
        page: pageNumber,
        limit: 10,
        keyword: search || undefined,
        status: statusFilter !== "" ? Number(statusFilter) : undefined,
      })
    )
  }, [dispatch, pageNumber, statusFilter, tenantPermissions.list])

  const handleSearch = e => {
    e.preventDefault()
    const params = new URLSearchParams(location.search)
    params.set("page", "1")
    navigate(`${location.pathname}?${params.toString()}`)
    dispatch(
      saGetAllTenants({
        page: 1,
        limit: 10,
        keyword: search || undefined,
        status: statusFilter !== "" ? Number(statusFilter) : undefined,
      })
    )
  }

  const handleReset = () => {
    setSearch("")
    setStatusFilter("")
    const params = new URLSearchParams(location.search)
    params.set("page", "1")
    navigate(`${location.pathname}?${params.toString()}`)
    dispatch(saGetAllTenants({ page: 1, limit: 10 }))
  }

  const handlePageChange = page => {
    const params = new URLSearchParams(location.search)
    params.set("page", page.toString())
    navigate(`${location.pathname}?${params.toString()}`)
  }

  const handleStatusToggle = tenant => {
    const currentStatus = Number(tenant.status)
    const nextStatus = currentStatus === 1 ? 0 : 1
    setConfirmModal({ open: true, tenant, action: nextStatus })
  }

  const handleLoginToTenant = tenant => {
    if (!tenantPermissions.login) {
      return
    }
    if (!tenant?._id || Number(tenant.status) !== 1) {
      dispatch(
        showAlert({
          type: "error",
          text: t("Only active tenants can be accessed."),
        })
      )
      return
    }
    setLoggingInTenantId(tenant._id)
    dispatch(
      saLoginToTenant({ id: tenant._id, useHardRedirect: true })
    )
      .then(res => {
        if (!res?.payload?.status) {
          setLoggingInTenantId(null)
          dispatch(
            showAlert({
              type: "error",
              text:
                res?.payload?.msg ||
                t("Failed to login to tenant. Please try again."),
            })
          )
        }
      })
      .catch(() => {
        setLoggingInTenantId(null)
      })
  }

  const handleRefreshTenantPermissions = tenant => {
    if (!tenantPermissions.update || !tenant?._id) {
      return
    }
    setRefreshingTenantId(tenant._id)
    dispatch(saRefreshTenantPermissions({ id: tenant._id }))
      .then(res => {
        if (res?.payload?.status) {
          dispatch(
            showAlert({
              type: "success",
              text: t("Tenant permissions refreshed successfully."),
            })
          )
        } else {
          dispatch(
            showAlert({
              type: "error",
              text:
                res?.payload?.msg ||
                t("Failed to refresh tenant permissions. Please try again."),
            })
          )
        }
      })
      .catch(() => {
        dispatch(
          showAlert({
            type: "error",
            text: t("Failed to refresh tenant permissions. Please try again."),
          })
        )
      })
      .finally(() => {
        setRefreshingTenantId(null)
      })
  }

  const confirmStatusChange = () => {
    const { tenant, action } = confirmModal
    if (!tenant?._id) {
      setConfirmModal({ open: false, tenant: null, action: "" })
      return
    }

    dispatch(
      saUpdateTenantStatus({ id: tenant._id, status: action })
    ).then(res => {
      if (res?.payload?.status) {
        dispatch(
          showAlert({
            type: "success",
            text: t("Tenant status updated successfully"),
          })
        )
        dispatch(
          saGetAllTenants({
            page: pageNumber,
            limit: 10,
            keyword: search || undefined,
            status: statusFilter !== "" ? Number(statusFilter) : undefined,
          })
        )
      } else {
        dispatch(
          showAlert({
            type: "error",
            text:
              res?.payload?.msg ||
              t("Failed to update tenant status. Please try again."),
          })
        )
      }
    })
    setConfirmModal({ open: false, tenant: null, action: "" })
  }

  const getTenantActionItems = tenant => [
    ...(tenantPermissions.list
      ? [
          {
            key: "view",
            content: (
              <>
                <i className="bx bx-show me-1" />
                {t("View")}
              </>
            ),
            onClick: () =>
              navigate(`${ROUTES.SA_TENANT_DETAILS}?id=${tenant._id}`),
          },
        ]
      : []),
    ...(tenantPermissions.update
      ? [
          {
            key: "edit",
            content: (
              <>
                <i className="bx bx-edit me-1" />
                {t("Edit")}
              </>
            ),
            onClick: () =>
              navigate(`${ROUTES.SA_TENANT_EDIT}?id=${tenant._id}`),
          },
          {
            key: "status",
            content: (
              <>
                <i
                  className={`bx ${
                    Number(tenant.status) === 1 ? "bx-pause" : "bx-play"
                  } me-1`}
                />
                {Number(tenant.status) === 1
                  ? t("Make Inactive")
                  : t("Activate")}
              </>
            ),
            disabled: tenantActionLoading,
            onClick: () => handleStatusToggle(tenant),
          },
        ]
      : []),
  ]

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs />
          <Row className="align-items-center mb-3">
            <Col>
              <h4 className="mb-0">{t("Tenants")}</h4>
              <p className="text-muted mb-0">
                {t("Manage all tenant accounts")}
              </p>
            </Col>
            {tenantPermissions.create && (
              <Col xs="auto">
                <Button
                  color="primary"
                  onClick={() => navigate(ROUTES.SA_TENANT_ADD)}
                >
                  <i className="bx bx-plus me-1" />
                  {t("Add Tenant")}
                </Button>
              </Col>
            )}
          </Row>

          <Card>
            <CardBody>
              {/* Filters */}
              <Row className="mb-3 g-2">
                <Col md={6}>
                  <form onSubmit={handleSearch}>
                    <InputGroup>
                      <Input
                        placeholder={t("Search by name, domain, or tenant ID...")}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                      />
                      <Button color="primary" type="submit">
                        <i className="bx bx-search" />
                      </Button>
                    </InputGroup>
                  </form>
                </Col>
                <Col md={3}>
                  <Input
                    type="select"
                    value={statusFilter}
                    onChange={e => {
                      setStatusFilter(e.target.value)
                      const params = new URLSearchParams(location.search)
                      params.set("page", "1")
                      navigate(`${location.pathname}?${params.toString()}`)
                    }}
                  >
                    <option value="">{t("All Statuses")}</option>
                    <option value="1">{t("Active")}</option>
                    <option value="0">{t("Inactive")}</option>
                    <option value="3">{t("Suspended")}</option>
                    <option value="4">{t("Pending")}</option>
                  </Input>
                </Col>
                <Col md={3}>
                  <Button
                    color="light"
                    className="w-100 d-flex align-items-center justify-content-center"
                    onClick={handleReset}
                    disabled={tenantsLoading}
                  >
                    <i className="mdi mdi-refresh me-1" style={{ fontSize: 18 }} />
                    {t("Reset")}
                  </Button>
                </Col>
              </Row>

              {tenantsLoading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                </div>
              ) : tenants.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  {t("No tenants found.")}
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                  <Table className="table align-middle table-nowrap mb-0">
                      <thead className="table-light full_th">
                        <tr>
                          <th>{t("Name")}</th>
                          <th>{t("Domain")}</th>
                          <th>{t("Contact Email")}</th>
                          <th>{t("Plan")}</th>
                          <th>{t("Subscription Expiry")}</th>
                          <th>{t("Status")}</th>
                          {tenantPermissions.login && (
                            <th style={{ minWidth: "120px" }}>{t("Login")}</th>
                          )}
                          <th style={{ minWidth: "fit-content", whiteSpace: "nowrap" }}>{t("Actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tenants.map((tenant, idx) => (
                          <tr key={tenant._id || idx}>
                            <td className="fw-medium">{tenant.name}</td>
                            <td>{tenant.domain || "—"}</td>
                            <td>{tenant.contactEmail || "—"}</td>
                            <td>
                              {tenant.subscription?.plan?.name ||
                                tenant.planName ||
                                "—"}
                            </td>
                            <td>
                              {tenant.subscription?.expiresAt
                                ? new Date(
                                    tenant.subscription.expiresAt
                                  ).toLocaleDateString()
                                : "—"}
                            </td>
                            <td>
                              <Badge
                                color={
                                  STATUS_COLORS[normalizeTenantStatusLabel(tenant)] || "secondary"
                                }
                                className="text-capitalize"
                              >
                                {translateStatusLabel(t, normalizeTenantStatusLabel(tenant))}
                              </Badge>
                            </td>
                            {tenantPermissions.login && (
                              <td>
                                <Button
                                  color="primary"
                                  size="sm"
                                  className="d-inline-flex align-items-center"
                                  onClick={() => handleLoginToTenant(tenant)}
                                  disabled={
                                    Boolean(loggingInTenantId) ||
                                    Number(tenant.status) !== 1
                                  }
                                  title={
                                    Number(tenant.status) !== 1
                                      ? t("Only active tenants can be accessed.")
                                      : undefined
                                  }
                                >
                                  {loggingInTenantId === tenant._id ? (
                                    <Spinner size="sm" className="me-1" />
                                  ) : (
                                    <i className="bx bx-log-in me-1" />
                                  )}
                                  {t("Login")}
                                </Button>
                              </td>
                            )}
                            <td>
                              <div className="gap-2 d-flex align-items-center">
                                {tenantPermissions.update && (
                                  <Button
                                    color="warning"
                                    outline
                                    size="sm"
                                    className="d-inline-flex align-items-center justify-content-center p-1"
                                    style={{ width: "2rem", height: "2rem" }}
                                    onClick={() => handleRefreshTenantPermissions(tenant)}
                                    disabled={Boolean(refreshingTenantId)}
                                    title={t("Refresh plan-based permissions")}
                                    aria-label={t("Refresh plan-based permissions")}
                                  >
                                    {refreshingTenantId === tenant._id ? (
                                      <Spinner size="sm" />
                                    ) : (
                                      <i className="mdi mdi-recycle font-size-16" />
                                    )}
                                  </Button>
                                )}
                                {hasRowActions && (
                                <TableActionsDropdown
                                  items={getTenantActionItems(tenant)}
                                />
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                  </Table>
                  </div>

                  <Paginations
                    currentPage={pageNumber}
                    totalPages={tenantsTotalPages}
                    onPageChange={handlePageChange}
                    totalCount={tenantsTotalCount}
                  />
                </>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      <ConfirmModal
        show={confirmModal.open}
        onCloseClick={() =>
          setConfirmModal({ open: false, tenant: null, action: "" })
        }
        onDeleteClick={confirmStatusChange}
        isLoading={tenantActionLoading}
        buttonText={
          Number(confirmModal.action) === 0
            ? t("Make Inactive")
            : t("Activate")
        }
        bodyTitle={
          Number(confirmModal.action) === 0
            ? t("Are you sure you want to make this tenant inactive?")
            : t("Are you sure you want to activate this tenant?")
        }
      />
    </React.Fragment>
  )
}

export default TenantsList
